import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Grid,
  IconButton,
  Avatar,
  Divider,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  RecordVoiceOver as RecordVoiceOverIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Grade as GradeIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PictureAsPdf as PdfIcon,
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  Assignment as AssignmentIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from "@mui/icons-material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import HtmlRenderer from "./HtmlRenderer";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import {
  useGetSpeakingCommentsQuery,
  useCreateSpeakingCommentMutation,
  useReplyToSpeakingCommentMutation,
  useDeleteSpeakingCommentMutation,
  useDeleteSpeakingCommentReplyMutation,
} from "../mockTestSlice";

/* ---------------------------------------------------------------
   Brand Colors
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   OET Grade Helper
--------------------------------------------------------------- */
function getOetGrade(marks) {
  if (marks == null) return null;
  const score = Number(marks);
  if (score >= 450) return { grade: "A", color: SUCCESS_GREEN };
  if (score >= 400) return { grade: "B", color: "#2196f3" };
  if (score >= 350) return { grade: "C+", color: "#ff9800" };
  if (score >= 300) return { grade: "C", color: "#ff9800" };
  if (score >= 200) return { grade: "D", color: BRAND_PRIMARY };
  return { grade: "E", color: BRAND_DARK };
}

/* ---------------------------------------------------------------
   IELTS Band Score Helper
--------------------------------------------------------------- */
function getIeltsBandInfo(band) {
  if (band == null) return null;
  const score = Number(band);
  if (score >= 8) return { color: SUCCESS_GREEN, label: "Expert" };
  if (score >= 7) return { color: "#2196f3", label: "Very Good" };
  if (score >= 6) return { color: "#03a9f4", label: "Competent" };
  if (score >= 5) return { color: "#ff9800", label: "Modest" };
  if (score >= 4) return { color: "#ff5722", label: "Limited" };
  return { color: BRAND_DARK, label: "Basic" };
}

/* ---------------------------------------------------------------
   Audio Player Component
--------------------------------------------------------------- */
function AudioPlayer({ audioUrl, audioName, index, label }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "#fff",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Play/Pause Button */}
        <IconButton
          onClick={handlePlayPause}
          sx={{
            width: 48,
            height: 48,
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            "&:hover": { bgcolor: BRAND_DARK },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        {/* Audio Info & Progress */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <MusicNoteIcon sx={{ fontSize: 16, color: BRAND_PRIMARY }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#1e293b" }}
              noWrap
            >
              {label || `Recording ${index + 1}`}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "#64748b", display: "block", mb: 1 }}
            noWrap
          >
            {audioName || "Audio Recording"}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                flex: 1,
                height: 4,
                bgcolor: "#e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${progressPercent}%`,
                  height: "100%",
                  bgcolor: BRAND_PRIMARY,
                  transition: "width 0.1s",
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", minWidth: 70 }}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        style={{ display: "none" }}
      />
    </Card>
  );
}

/* ---------------------------------------------------------------
   Speaking Card Display (Roleplayer/Candidate Cards) - OET
--------------------------------------------------------------- */
function SpeakingCardDisplay({ card, index }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: BRAND_BG,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Chip
            label={card.cardType || `Card ${card.cardNumber}`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              fontWeight: 600,
            }}
          />
          <HtmlRenderer
            content={card.roleLabel}
            component="span"
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: BRAND_PRIMARY,
            }}
          />
        </Box>
      </Box>

      <CardContent sx={{ p: 2.5 }}>
        <TextHighlighterWrapper>
          {/* Setting */}
          {card.setting && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Setting:
              </Typography>
              <HtmlRenderer
                content={card.setting}
                component="div"
                sx={{ fontWeight: 500, color: "#1e293b" }}
              />
            </Box>
          )}

          {/* Description */}
          {card.description && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Description:
              </Typography>
              <HtmlRenderer
                content={card.description}
                component="div"
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                  color: "#374151",
                }}
              />
            </Box>
          )}

          {/* Tasks */}
          {card.tasks && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Tasks:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "#f8fafc",
                  borderRadius: 1,
                }}
              >
                <HtmlRenderer
                  content={card.tasks}
                  component="div"
                  sx={{
                    whiteSpace: "pre-line",
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    color: "#374151",
                  }}
                />
              </Paper>
            </Box>
          )}
        </TextHighlighterWrapper>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------
   Main Card Section (Contains Roleplayer & Candidate Cards) - OET
--------------------------------------------------------------- */
function MainCardSection({ mainCard, index, audios }) {
  const cards = mainCard.cards || [];
  // Find the audio for this main card (if any)
  const audioForCard = audios[index];

  return (
    <Box sx={{ mb: 4 }}>
      {/* Main Card Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          pb: 1,
          borderBottom: `2px solid ${BRAND_PRIMARY}`,
        }}
      >
        <Avatar
          sx={{
            bgcolor: BRAND_PRIMARY,
            width: 36,
            height: 36,
          }}
        >
          {mainCard.mainCardNumber || index + 1}
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
          Role-Play {mainCard.mainCardNumber || index + 1}
        </Typography>
      </Box>

      {/* Cards Grid (Roleplayer & Candidate) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {cards.map((card, cardIdx) => (
          <Grid item xs={12} md={6} key={card._id || cardIdx}>
            <SpeakingCardDisplay card={card} index={cardIdx} />
          </Grid>
        ))}
      </Grid>

      {/* Audio for this Main Card */}
      {audioForCard && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "#64748b", mb: 1, fontWeight: 600 }}
          >
            Your Recording for Role-Play {index + 1}:
          </Typography>
          <AudioPlayer
            audioUrl={audioForCard.audioUrl}
            audioName={audioForCard.originalName}
            index={index}
          />
        </Box>
      )}
    </Box>
  );
}

/* ---------------------------------------------------------------
   IELTS Speaking Part Section (Part 1, 2, 3)
--------------------------------------------------------------- */
function IeltsSpeakingPartSection({ part, partIndex, speakingAudios }) {
  const questions = part.questions || [];

  // Find audios for this part
  const partAudios = speakingAudios.filter(
    (audio) => audio.speakingPartId === part._id
  );

  // Create a map of questionId to audio
  const audioByQuestionId = {};
  partAudios.forEach((audio) => {
    if (audio.questionId) {
      audioByQuestionId[audio.questionId] = audio;
    }
  });

  return (
    <Box sx={{ mb: 4 }}>
      {/* Part Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          pb: 1,
          borderBottom: `2px solid ${BRAND_PRIMARY}`,
        }}
      >
        <Avatar
          sx={{
            bgcolor: BRAND_PRIMARY,
            width: 36,
            height: 36,
          }}
        >
          {part.partNumber || partIndex + 1}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
            {part.title || `Part ${part.partNumber || partIndex + 1}`}
          </Typography>
          {part.instructions && (
            <Typography
              variant="body2"
              sx={{ color: "#64748b", mt: 0.5, fontStyle: "italic" }}
            >
              {part.instructions}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Questions */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {questions.map((question, qIndex) => {
          const questionAudio = audioByQuestionId[question._id];

          return (
            <Card
              key={question._id || qIndex}
              elevation={0}
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {/* Question Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: BRAND_BG,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <Chip
                    label={`Question ${qIndex + 1} of Part ${part.partNumber || partIndex + 1}`}
                    size="small"
                    sx={{
                      bgcolor: BRAND_PRIMARY,
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                  {question.questionHeading && question.questionHeading !== "EXAMPLE" && (
                    <HtmlRenderer
                      content={question.questionHeading}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: BRAND_DARK,
                      }}
                    />
                  )}
                </Box>
              </Box>

              <CardContent sx={{ p: 2.5 }}>
                <TextHighlighterWrapper>
                  {/* Question Text */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: "#f8fafc",
                      borderRadius: 1,
                      mb: questionAudio ? 2 : 0,
                    }}
                  >
                    <HtmlRenderer
                      content={question.questions}
                      sx={{
                        whiteSpace: "pre-line",
                        fontSize: "0.95rem",
                        lineHeight: 1.7,
                        color: "#374151",
                      }}
                    />
                  </Paper>

                  {/* Audio for this question */}
                  {questionAudio && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#64748b", mb: 1, fontWeight: 600 }}
                      >
                        Your Response:
                      </Typography>
                      <AudioPlayer
                        audioUrl={questionAudio.audioUrl}
                        audioName={questionAudio.originalName}
                        index={qIndex}
                        label={`Part ${part.partNumber} - Q${question.order || qIndex + 1}`}
                      />
                    </Box>
                  )}
                </TextHighlighterWrapper>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Show remaining audios for this part that aren't matched to questions */}
      {partAudios.length > 0 && questions.length === 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "#64748b", mb: 1, fontWeight: 600 }}
          >
            Your Recording{partAudios.length > 1 ? "s" : ""} for Part{" "}
            {part.partNumber || partIndex + 1}:
          </Typography>
          <Grid container spacing={2}>
            {partAudios.map((audio, idx) => (
              <Grid item xs={12} md={6} key={audio._id || idx}>
                <AudioPlayer
                  audioUrl={audio.audioUrl}
                  audioName={audio.originalName}
                  index={idx}
                  label={`Part ${part.partNumber} Recording ${idx + 1}`}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

/* ---------------------------------------------------------------
   Evaluation Section with PDF Viewer and Comments
--------------------------------------------------------------- */
function EvaluationSection({
  isEvaluated,
  marks,
  grade,
  adminComments,
  evaluationPdf,
  checkingStatus,
  evaluatorName,
  speakingAnswerId,
  isIelts,
}) {
  const gradeInfo =
    marks !== null && marks !== undefined
      ? isIelts
        ? getIeltsBandInfo(marks)
        : getOetGrade(marks)
      : null;

  // Comment State
  const [activeCommentSection, setActiveCommentSection] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  // Comment API Hooks
  const [createComment, { isLoading: isCreating }] =
    useCreateSpeakingCommentMutation();
  const [replyToComment, { isLoading: isReplying }] =
    useReplyToSpeakingCommentMutation();
  const [deleteComment] = useDeleteSpeakingCommentMutation();
  const [deleteReply] = useDeleteSpeakingCommentReplyMutation();

  // Fetch comments
  const {
    data: commentsData,
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetSpeakingCommentsQuery(
    { speakingAnswerId },
    { skip: !speakingAnswerId }
  );

  // Handle comment submission
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      await createComment({
        speakingAnswerId,
        commentText: newComment.trim(),
      }).unwrap();

      setNewComment("");
      refetchComments();
      setToast({
        open: true,
        text: "Comment sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating comment:", error);
      setToast({
        open: true,
        text: "Failed to send comment",
        severity: "error",
      });
    }
  };

  // Handle reply submission
  const handleSubmitReply = async (commentId) => {
    if (!replyText.trim()) return;

    try {
      await replyToComment({
        speakingAnswerId,
        commentId,
        commentText: replyText.trim(),
      }).unwrap();

      setReplyText("");
      setReplyingTo(null);
      refetchComments();
      setToast({
        open: true,
        text: "Reply sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error replying to comment:", error);
      setToast({ open: true, text: "Failed to send reply", severity: "error" });
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    setDeleteItem({ type: "comment", commentId });
    setDeleteConfirmOpen(true);
  };

  // Handle delete reply
  const handleDeleteReply = async (commentId, replyId) => {
    setDeleteItem({ type: "reply", commentId, replyId });
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteItem) return;

    try {
      if (deleteItem.type === "comment") {
        await deleteComment({
          speakingAnswerId,
          commentId: deleteItem.commentId,
        }).unwrap();
        setToast({
          open: true,
          text: "Comment deleted successfully",
          severity: "success",
        });
      } else if (deleteItem.type === "reply") {
        await deleteReply({
          speakingAnswerId,
          commentId: deleteItem.commentId,
          replyId: deleteItem.replyId,
        }).unwrap();
        setToast({
          open: true,
          text: "Reply deleted successfully",
          severity: "success",
        });
      }
      refetchComments();
    } catch (error) {
      console.error("Error deleting:", error);
      setToast({ open: true, text: "Failed to delete", severity: "error" });
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
    }
  };

  // Don't show evaluation section until test is evaluated
  if (!isEvaluated) {
    return null;
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
          borderRadius: 2,
          mt: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: isEvaluated ? SUCCESS_BG : "#f8fafc",
            borderBottom: `1px solid ${
              isEvaluated ? SUCCESS_GREEN : "#e2e8f0"
            }`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isEvaluated ? (
              <CheckCircleIcon sx={{ color: SUCCESS_GREEN }} />
            ) : (
              <ScheduleIcon sx={{ color: "#64748b" }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isEvaluated ? SUCCESS_GREEN : "#64748b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {isEvaluated ? "Assessor Evaluation" : "Pending Evaluation"}
            </Typography>
          </Box>

          {isEvaluated && marks !== null && marks !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: gradeInfo?.color || BRAND_PRIMARY,
                }}
              >
                {marks}
              </Typography>
              {isIelts ? (
                <Chip
                  icon={<GradeIcon sx={{ fontSize: 16 }} />}
                  label={`Band ${marks}`}
                  sx={{
                    bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                    color: gradeInfo?.color || BRAND_PRIMARY,
                    fontWeight: 600,
                  }}
                />
              ) : (
                <Chip
                  icon={<GradeIcon sx={{ fontSize: 16 }} />}
                  label={`Grade ${grade || gradeInfo?.grade}`}
                  sx={{
                    bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                    color: gradeInfo?.color || BRAND_PRIMARY,
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        <CardContent>
          {isEvaluated ? (
            <Box>
              {/* Comments */}
              {adminComments && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <CommentIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                      Examiner's Comments
                    </Typography>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      borderLeft: `3px solid ${BRAND_PRIMARY}`,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        color: "#374151",
                      }}
                    >
                      {adminComments}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* PDF Viewer with Comment Toggle */}
              {evaluationPdf && (
                <Box sx={{ mb: 3 }}>
                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={evaluationPdf}
                      download
                      sx={{
                        borderColor: BRAND_PRIMARY,
                        color: BRAND_PRIMARY,
                        "&:hover": {
                          borderColor: BRAND_DARK,
                          bgcolor: BRAND_BG,
                        },
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Download PDF
                    </Button>
                    {speakingAnswerId && (
                      <Button
                        variant={
                          activeCommentSection ? "outlined" : "contained"
                        }
                        size="medium"
                        onClick={() =>
                          setActiveCommentSection(!activeCommentSection)
                        }
                        startIcon={
                          activeCommentSection ? <CloseIcon /> : <SendIcon />
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 4,
                          py: 1,
                          borderRadius: 3,
                          minWidth: 180,
                          bgcolor: activeCommentSection
                            ? "transparent"
                            : BRAND_PRIMARY,
                          borderColor: BRAND_PRIMARY,
                          borderWidth: 2,
                          color: activeCommentSection ? BRAND_PRIMARY : "white",
                          "&:hover": {
                            bgcolor: activeCommentSection
                              ? BRAND_BG
                              : BRAND_DARK,
                            borderColor: BRAND_DARK,
                            borderWidth: 2,
                            transform: "translateY(-1px)",
                            boxShadow: `0 8px 25px rgba(157, 34, 53, 0.25)`,
                          },
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: activeCommentSection
                            ? `0 2px 8px rgba(157, 34, 53, 0.15)`
                            : `0 4px 14px rgba(157, 34, 53, 0.4)`,
                        }}
                      >
                        {activeCommentSection
                          ? "Exit Comment Mode"
                          : "Start Comment"}
                      </Button>
                    )}
                  </Box>

                  {/* Split Screen Layout when comments active, Single PDF otherwise */}
                  {activeCommentSection && speakingAnswerId ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                        gap: 3,
                        minHeight: 700,
                      }}
                    >
                      {/* Left Side - PDF Viewer */}
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "#f8fafc",
                          borderRadius: 3,
                          border: "2px solid #e2e8f0",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
                            color: "white",
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: 18 }}>📄</Typography>
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                            >
                              Speaking Feedback PDF
                            </Typography>
                          </Box>
                          <Chip
                            label="Live View"
                            size="small"
                            sx={{
                              bgcolor: "#475569",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                        <Box sx={{ height: 650, width: "100%" }}>
                          <iframe
                            src={`${evaluationPdf}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              background: "white",
                            }}
                            title="Speaking Feedback PDF"
                          />
                        </Box>
                      </Paper>

                      {/* Right Side - Chat Interface */}
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "white",
                          borderRadius: 3,
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Chat Header */}
                        <Box
                          sx={{
                            background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
                            p: 2,
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "rgba(255,255,255,0.2)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CommentIcon sx={{ color: "white" }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                sx={{
                                  color: "white",
                                  fontWeight: 700,
                                  fontSize: "1.125rem",
                                }}
                              >
                                Assessment Discussion
                              </Typography>
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.8)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {commentsData?.comments?.length || 0} messages
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  bgcolor: "#4ade80",
                                  borderRadius: "50%",
                                  animation:
                                    "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                                  "@keyframes pulse": {
                                    "0%, 100%": { opacity: 1 },
                                    "50%": { opacity: 0.5 },
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.9)",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                }}
                              >
                                Active
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Messages Area */}
                        <Box
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 2,
                            maxHeight: 500,
                            bgcolor: "#f8fafc",
                          }}
                        >
                          {commentsLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 8,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: "#64748b",
                                }}
                              >
                                <CircularProgress
                                  size={16}
                                  sx={{ color: BRAND_PRIMARY }}
                                />
                                <Typography sx={{ fontSize: "0.875rem" }}>
                                  Loading messages...
                                </Typography>
                              </Box>
                            </Box>
                          ) : commentsData?.comments?.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {commentsData.comments.map((comment, index) => (
                                <Box key={comment._id || index}>
                                  {/* Main Comment Message */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "start",
                                      gap: 1.5,
                                      justifyContent:
                                        comment.createdByModel === "student"
                                          ? "flex-start"
                                          : "flex-end",
                                    }}
                                  >
                                    {comment.createdByModel === "student" && (
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
                                          boxShadow:
                                            "0 4px 12px rgba(157, 34, 53, 0.3)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {comment.createdBy?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "S"}
                                        </Typography>
                                      </Avatar>
                                    )}

                                    <Box
                                      sx={{
                                        maxWidth: "75%",
                                        order:
                                          comment.createdByModel === "student"
                                            ? 2
                                            : 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          borderRadius: 4,
                                          px: 2,
                                          py: 1.5,
                                          boxShadow:
                                            "0 2px 8px rgba(0,0,0,0.1)",
                                          background:
                                            comment.createdByModel === "student"
                                              ? `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`
                                              : "white",
                                          color:
                                            comment.createdByModel === "student"
                                              ? "white"
                                              : "#374151",
                                          borderBottomLeftRadius:
                                            comment.createdByModel === "student"
                                              ? 4
                                              : 16,
                                          borderBottomRightRadius:
                                            comment.createdByModel === "student"
                                              ? 16
                                              : 4,
                                          border:
                                            comment.createdByModel === "student"
                                              ? "none"
                                              : "2px solid #e2e8f0",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {comment.createdBy?.fullName ||
                                              (comment.createdByModel ===
                                              "student"
                                                ? "Student"
                                                : "Assessor")}
                                          </Typography>
                                          <Chip
                                            label={
                                              comment.createdByModel ===
                                              "student"
                                                ? "Student"
                                                : "Assessor"
                                            }
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: "0.65rem",
                                              fontWeight: 600,
                                              bgcolor:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.2)"
                                                  : "#dcfce7",
                                              color:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.8)"
                                                  : "#16a34a",
                                            }}
                                          />
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.875rem",
                                            lineHeight: 1.6,
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {comment.commentText}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mt: 1.5,
                                            pt: 1,
                                            borderTop: `1px solid ${
                                              comment.createdByModel ===
                                              "student"
                                                ? "rgba(255,255,255,0.2)"
                                                : "#e2e8f0"
                                            }`,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "0.7rem",
                                              color:
                                                comment.createdByModel ===
                                                "student"
                                                  ? "rgba(255,255,255,0.7)"
                                                  : "#64748b",
                                            }}
                                          >
                                            {new Date(
                                              comment.createdAt
                                            ).toLocaleString("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <Button
                                              size="small"
                                              onClick={() => {
                                                setReplyingTo(
                                                  replyingTo === comment._id
                                                    ? null
                                                    : comment._id
                                                );
                                                setReplyText("");
                                              }}
                                              sx={{
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                textTransform: "none",
                                                minWidth: "auto",
                                                p: 0.5,
                                                color:
                                                  comment.createdByModel ===
                                                  "student"
                                                    ? "rgba(255,255,255,0.8)"
                                                    : BRAND_PRIMARY,
                                                "&:hover": {
                                                  color:
                                                    comment.createdByModel ===
                                                    "student"
                                                      ? "white"
                                                      : BRAND_DARK,
                                                },
                                              }}
                                            >
                                              Reply
                                            </Button>
                                            {comment.createdByModel ===
                                              "student" && (
                                              <Button
                                                size="small"
                                                onClick={() =>
                                                  handleDeleteComment(
                                                    comment._id
                                                  )
                                                }
                                                sx={{
                                                  fontSize: "0.7rem",
                                                  fontWeight: 600,
                                                  textTransform: "none",
                                                  minWidth: "auto",
                                                  p: 0.5,
                                                  color: "#fca5a5",
                                                  "&:hover": {
                                                    color: "#ef4444",
                                                  },
                                                }}
                                              >
                                                Delete
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {comment.createdByModel !== "student" && (
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          background:
                                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                          boxShadow:
                                            "0 4px 12px rgba(16, 185, 129, 0.3)",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {comment.createdBy?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "A"}
                                        </Typography>
                                      </Avatar>
                                    )}
                                  </Box>

                                  {/* Reply Input */}
                                  {replyingTo === comment._id && (
                                    <Box
                                      sx={{
                                        ml:
                                          comment.createdByModel === "student"
                                            ? 7
                                            : 0,
                                        mr:
                                          comment.createdByModel !== "student"
                                            ? 7
                                            : 0,
                                        mt: 1,
                                      }}
                                    >
                                      <Paper
                                        variant="outlined"
                                        sx={{ p: 1.5, bgcolor: "#f8fafc" }}
                                      >
                                        <TextField
                                          fullWidth
                                          multiline
                                          rows={2}
                                          placeholder="Type your reply..."
                                          value={replyText}
                                          onChange={(e) =>
                                            setReplyText(e.target.value)
                                          }
                                          sx={{ mb: 1 }}
                                        />
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            justifyContent: "flex-end",
                                          }}
                                        >
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              setReplyingTo(null);
                                              setReplyText("");
                                            }}
                                            sx={{ textTransform: "none" }}
                                          >
                                            Cancel
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() =>
                                              handleSubmitReply(comment._id)
                                            }
                                            disabled={
                                              !replyText.trim() || isReplying
                                            }
                                            sx={{
                                              bgcolor: BRAND_PRIMARY,
                                              "&:hover": {
                                                bgcolor: BRAND_DARK,
                                              },
                                              textTransform: "none",
                                            }}
                                          >
                                            Send Reply
                                          </Button>
                                        </Box>
                                      </Paper>
                                    </Box>
                                  )}

                                  {/* Replies */}
                                  {comment.replies &&
                                    comment.replies.length > 0 && (
                                      <Box
                                        sx={{
                                          ml:
                                            comment.createdByModel === "student"
                                              ? 7
                                              : 0,
                                          mr:
                                            comment.createdByModel !== "student"
                                              ? 7
                                              : 0,
                                          mt: 1,
                                        }}
                                      >
                                        {comment.replies.map((reply) => (
                                          <Box
                                            key={reply._id}
                                            sx={{
                                              display: "flex",
                                              alignItems: "start",
                                              gap: 1,
                                              mb: 1,
                                              justifyContent:
                                                reply.repliedByModel ===
                                                "student"
                                                  ? "flex-start"
                                                  : "flex-end",
                                            }}
                                          >
                                            {reply.repliedByModel ===
                                              "student" && (
                                              <Avatar
                                                sx={{
                                                  width: 28,
                                                  height: 28,
                                                  bgcolor: BRAND_PRIMARY,
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                {reply.repliedBy?.fullName
                                                  ?.charAt(0)
                                                  ?.toUpperCase() || "S"}
                                              </Avatar>
                                            )}

                                            <Paper
                                              variant="outlined"
                                              sx={{
                                                p: 1,
                                                bgcolor:
                                                  reply.repliedByModel ===
                                                  "student"
                                                    ? BRAND_BG
                                                    : "#f0fdf4",
                                                borderColor:
                                                  reply.repliedByModel ===
                                                  "student"
                                                    ? BRAND_PRIMARY
                                                    : "#86efac",
                                                maxWidth: "80%",
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontSize: "0.75rem",
                                                  fontWeight: 600,
                                                  mb: 0.5,
                                                }}
                                              >
                                                {reply.repliedBy?.fullName ||
                                                  (reply.repliedByModel ===
                                                  "student"
                                                    ? "Student"
                                                    : "Assessor")}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: "0.8rem",
                                                  mb: 0.5,
                                                }}
                                              >
                                                {reply.commentText}
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: "0.65rem",
                                                    color: "#64748b",
                                                  }}
                                                >
                                                  {new Date(
                                                    reply.createdAt
                                                  ).toLocaleString("en-US", {
                                                    month: "short",
                                                    day: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                  })}
                                                </Typography>
                                                {reply.repliedByModel ===
                                                  "student" && (
                                                  <Button
                                                    size="small"
                                                    onClick={() =>
                                                      handleDeleteReply(
                                                        comment._id,
                                                        reply._id
                                                      )
                                                    }
                                                    sx={{
                                                      fontSize: "0.65rem",
                                                      minWidth: "auto",
                                                      p: 0.25,
                                                      color: "#ef4444",
                                                    }}
                                                  >
                                                    Delete
                                                  </Button>
                                                )}
                                              </Box>
                                            </Paper>

                                            {reply.repliedByModel !==
                                              "student" && (
                                              <Avatar
                                                sx={{
                                                  width: 28,
                                                  height: 28,
                                                  bgcolor: "#10b981",
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                {reply.repliedBy?.fullName
                                                  ?.charAt(0)
                                                  ?.toUpperCase() || "A"}
                                              </Avatar>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: "50%",
                                  bgcolor: BRAND_BG,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mx: "auto",
                                  mb: 2,
                                }}
                              >
                                <CommentIcon
                                  sx={{ fontSize: 32, color: BRAND_PRIMARY }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  mb: 1,
                                }}
                              >
                                No messages yet
                              </Typography>
                              <Typography
                                sx={{ color: "#64748b", fontSize: "0.875rem" }}
                              >
                                Start a conversation by sending a message below
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Message Input */}
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "white",
                            borderTop: "1px solid #e2e8f0",
                          }}
                        >
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Ask a question or discuss the evaluation..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            sx={{
                              mb: 1.5,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                              },
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "0.75rem", color: "#64748b" }}
                            >
                              {newComment.length}/500 characters
                            </Typography>
                            <Button
                              variant="contained"
                              endIcon={<SendIcon />}
                              onClick={handleSubmitComment}
                              disabled={!newComment.trim() || isCreating}
                              sx={{
                                bgcolor: BRAND_PRIMARY,
                                "&:hover": { bgcolor: BRAND_DARK },
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                px: 3,
                              }}
                            >
                              {isCreating ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                "Send Message"
                              )}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    /* Single PDF View (No Comments) */
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: "#f8fafc",
                        borderRadius: 3,
                        border: "2px solid #e2e8f0",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          background:
                            "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
                          color: "white",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography sx={{ fontSize: 18 }}>📄</Typography>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                          >
                            Speaking Feedback PDF
                          </Typography>
                        </Box>
                        <Chip
                          label="Preview"
                          size="small"
                          sx={{
                            bgcolor: "#475569",
                            color: "white",
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>
                      <Box sx={{ height: 750, width: "100%" }}>
                        <iframe
                          src={`${evaluationPdf}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            background: "white",
                          }}
                          title="Speaking Feedback PDF"
                        />
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}

              {!adminComments && !evaluationPdf && (
                <Typography sx={{ color: "#64748b" }}>
                  Your speaking has been evaluated. Score and grade are shown
                  above.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: BRAND_BG,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 32, color: BRAND_LIGHT }} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: BRAND_PRIMARY, mb: 1 }}
              >
                Evaluation in Progress
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                Your speaking will be evaluated within 24 hours. Please check
                back later for your results.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this{" "}
            {deleteItem?.type === "comment" ? "comment" : "reply"}? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.text}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------------------------------------------------------------
   Main SpeakingSectionReview Component
--------------------------------------------------------------- */
export default function SpeakingSectionReview({
  speakingData = {},
  submissionData = {},
  // Notes props
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
}) {
  // Extract test data - handle both OET (mainCards) and IELTS (parts)
  const test = submissionData.test || speakingData.test || speakingData;
  const mainCards = test.mainCards || [];
  const parts = test.parts || []; // IELTS speaking parts
  const testTitle = test.title || "";
  const discipline = test.discipline || "";
  const examType = submissionData.examType || test.examType || "";
  const isIelts =
    examType === "ielts" || (parts.length > 0 && mainCards.length === 0);

  // Submission data
  const speakingAudios = submissionData.speakingAudios || [];
  const speakingAnswerId = submissionData._id || submissionData.speakingAnswerId || null;

  // Handle nested pdfUrl structure: can be string or object with pdfUrl property
  // Priority: pdfUrl.pdfUrl > pdfUrl (string) > accessorFeedBackInPdf.pdfUrl
  const evaluationPdf =
    typeof submissionData.pdfUrl === "string"
      ? submissionData.pdfUrl
      : submissionData.pdfUrl?.pdfUrl ||
        submissionData.accessorFeedBackInPdf?.pdfUrl ||
        "";

  // Handle different score field names
  const marks =
    submissionData.avgScore ?? submissionData.score ?? submissionData.marks;
  const grade = submissionData.avgGrade || submissionData.grade || "";
  const adminComments = submissionData.accessorFeedBack || submissionData.feedback || "";
  const checkingStatus = submissionData.checkingStatus || "";
  const evaluatorName = submissionData.evaluatorId?.fullName || "";
  const isEvaluated =
    (marks !== null && marks !== undefined) ||
    !!evaluationPdf ||
    checkingStatus === "checked";

  // Debug logging
  console.log("SpeakingSectionReview Debug:", {
    submissionData,
    speakingAnswerId,
    evaluationPdf,
    pdfUrl_raw: submissionData.pdfUrl,
    pdfUrl_type: typeof submissionData.pdfUrl,
    pdfUrl_nested: submissionData.pdfUrl?.pdfUrl,
    accessorFeedBackInPdf: submissionData.accessorFeedBackInPdf,
    marks,
    grade,
    adminComments,
    checkingStatus,
    isEvaluated,
    speakingAudios,
    mainCards,
    parts,
    isIelts,
    testTitle,
  });

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Card
        elevation={0}
        sx={{
          mb: 0,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left - Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: BRAND_PRIMARY,
                width: 40,
                height: 40,
              }}
            >
              <RecordVoiceOverIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Speaking Module Review
              </Typography>
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {testTitle ||
                  (isIelts
                    ? `${parts.length} Part${parts.length !== 1 ? "s" : ""}`
                    : `${mainCards.length} Role-Play${
                        mainCards.length !== 1 ? "s" : ""
                      }`)}
                {discipline &&
                  ` • ${
                    discipline.charAt(0).toUpperCase() + discipline.slice(1)
                  }`}
              </Typography>
            </Box>
          </Box>

          {/* Right - Status */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {isIelts && (
              <Chip
                label="IELTS"
                size="small"
                sx={{
                  bgcolor: "#3b82f620",
                  color: "#3b82f6",
                  fontWeight: 600,
                }}
              />
            )}
            <Chip
              icon={
                isEvaluated ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ScheduleIcon sx={{ fontSize: 16 }} />
                )
              }
              label={
                checkingStatus === "pending"
                  ? "Pending Evaluation"
                  : isEvaluated
                    ? "Evaluated"
                    : "Pending"
              }
              sx={{
                bgcolor: isEvaluated ? SUCCESS_BG : BRAND_BG,
                color: isEvaluated ? SUCCESS_GREEN : BRAND_PRIMARY,
                fontWeight: 600,
              }}
            />
            {isEvaluated && marks !== null && marks !== undefined && (
              <Chip
                label={
                  isIelts ? `Band ${marks}` : `Score: ${marks}/500`
                }
                sx={{
                  bgcolor: `${
                    (isIelts
                      ? getIeltsBandInfo(marks)?.color
                      : getOetGrade(marks)?.color) || BRAND_PRIMARY
                  }20`,
                  color:
                    (isIelts
                      ? getIeltsBandInfo(marks)?.color
                      : getOetGrade(marks)?.color) || BRAND_PRIMARY,
                  fontWeight: 700,
                }}
              />
            )}
            {/* Notes Button */}
            {setNotesModalOpen && (
              <Tooltip title="View Notes" placement="left" arrow>
                <IconButton
                  onClick={() => setNotesModalOpen(true)}
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    color: "#fff",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: BRAND_DARK,
                    },
                  }}
                >
                  <Badge
                    badgeContent={notes?.length || 0}
                    color="error"
                    overlap="circular"
                  >
                    <StickyNote2Icon sx={{ fontSize: 18 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>

      {/* Content */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* IELTS Speaking Parts */}
        {isIelts && parts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <QuestionAnswerIcon sx={{ color: BRAND_PRIMARY }} />
              Speaking Parts & Your Responses
            </Typography>
            {parts.map((part, idx) => (
              <IeltsSpeakingPartSection
                key={part._id || idx}
                part={part}
                partIndex={idx}
                speakingAudios={speakingAudios}
              />
            ))}
          </Box>
        )}

        {/* OET Speaking Cards (Role-Plays) */}
        {!isIelts && mainCards.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AssignmentIcon sx={{ color: BRAND_PRIMARY }} />
              Role-Play Cards
            </Typography>
            {mainCards.map((mainCard, idx) => (
              <MainCardSection
                key={mainCard._id || idx}
                mainCard={mainCard}
                index={idx}
                audios={speakingAudios}
              />
            ))}
          </Box>
        )}

        {/* Audio Submissions Section - Show only if IELTS has unmatched audios or for general display */}
        {!isIelts && speakingAudios.length > 0 && mainCards.length === 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <VolumeUpIcon sx={{ color: BRAND_PRIMARY }} />
              Your Audio Submissions ({speakingAudios.length})
            </Typography>
            <Grid container spacing={2}>
              {speakingAudios.map((audio, idx) => (
                <Grid item xs={12} md={6} key={audio._id || idx}>
                  <AudioPlayer
                    audioUrl={audio.audioUrl}
                    audioName={audio.originalName}
                    index={idx}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Evaluation Section */}
        <EvaluationSection
          isEvaluated={isEvaluated}
          marks={marks}
          grade={grade}
          adminComments={adminComments}
          evaluationPdf={evaluationPdf}
          checkingStatus={checkingStatus}
          evaluatorName={evaluatorName}
          speakingAnswerId={speakingAnswerId}
          isIelts={isIelts}
        />
      </Box>

      {/* Notes Modal */}
      {setNotesModalOpen && (
        <NotesModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          notes={notes}
        />
      )}
    </Box>
  );
}
